import React, { useMemo } from 'react';
import { countExpressions, getEmptyFilterExpression, getUniqueFieldNames, StringConditionType } from '@fabric-ui/components';
import { isObjectArrayField } from '../useFilterFields';
import { ExpressionBuilder, ExpressionBuilderProvider, pickExpressionBuilder } from './ExpressionBuilderContext';
import { FilterConfig, FilterConfigProvider, pickFilterConfig } from './FilterContext';
import { useFieldProcessing } from './hooks/useFieldProcessing';
import { ExpressionBuilderProps, NewExpressionBuilderBase } from './NewExpressionBuilderBase';

const NewExpressionBuilder: React.FC<ExpressionBuilderProps> = props => {
  const [rawConfig, rest] = pickFilterConfig(props);

  const config = useMemo<FilterConfig>(() => {
    const c: FilterConfig = {};
    if (rawConfig.size !== undefined) c.size = rawConfig.size === 'xSmall' ? 'xSmall' : 'small';
    if (rawConfig.isVertical !== undefined) c.isVertical = rawConfig.isVertical;
    if (rawConfig.fullWidth !== undefined) c.fullWidth = rawConfig.fullWidth;
    if (rawConfig.disabled !== undefined) c.disabled = rawConfig.disabled;
    if (rawConfig.showAdvanced !== undefined) c.showAdvanced = rawConfig.showAdvanced;
    if (rawConfig.enableQueryObj !== undefined) c.enableQueryObj = rawConfig.enableQueryObj;
    if (rawConfig.indicateExcludedValues !== undefined) c.indicateExcludedValues = rawConfig.indicateExcludedValues;
    if (rawConfig.activeProjName !== undefined) c.activeProjName = rawConfig.activeProjName;
    if (rawConfig.containedDatePicker !== undefined) c.containedDatePicker = rawConfig.containedDatePicker;
    if (rawConfig.mode !== undefined) c.mode = rawConfig.mode;
    return c;
  }, [rawConfig]);

  const [expressionBuilder, internalProps] = pickExpressionBuilder(rest);
  const { filter, setFilter, fieldSelectionLimit, rowsLimit } = internalProps;

  const { supportParser = false, fields = [] } = expressionBuilder;

  const effectiveFilter = useMemo(
    () => filter ?? getEmptyFilterExpression(null, supportParser ? StringConditionType.EQUALS : undefined),
    [filter, supportParser]
  );

  const selectedFieldNames = useMemo(
    () => (fieldSelectionLimit ? getUniqueFieldNames(effectiveFilter) : new Set<string>()),
    [fieldSelectionLimit, effectiveFilter]
  );
  const isOverFieldNamesLimit = fieldSelectionLimit && selectedFieldNames.size >= fieldSelectionLimit.limit;
  const isOverExpressionsLimit = rowsLimit && countExpressions(effectiveFilter) >= rowsLimit.limit;
  const disableMessage = isOverExpressionsLimit ? rowsLimit.message : undefined;
  const fieldSelectionMessage = isOverFieldNamesLimit ? fieldSelectionLimit.message : undefined;

  const filterableFields = useMemo(() => fields.filter(field => !isObjectArrayField(field)), [fields]);
  const modifiedFields = useFieldProcessing(filterableFields, selectedFieldNames, isOverFieldNamesLimit);

  const fullExpressionBuilder = useMemo<ExpressionBuilder>(
    () => ({
      ...expressionBuilder,
      fields: modifiedFields,
      isOverExpressionsLimit,
      disableMessage,
      fieldSelectionMessage,
    }),
    [expressionBuilder, modifiedFields, isOverExpressionsLimit, disableMessage, fieldSelectionMessage]
  );

  return (
    <FilterConfigProvider config={config}>
      <ExpressionBuilderProvider options={fullExpressionBuilder}>
        <NewExpressionBuilderBase filter={filter} setFilter={setFilter} rowsLimit={rowsLimit} />
      </ExpressionBuilderProvider>
    </FilterConfigProvider>
  );
};

export default NewExpressionBuilder;
